// Video catalog API service
const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

async function videoRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const correlationId = generateCorrelationId();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as { error: string }).error || "Request failed");
  }

  return data as T;
}

export interface VideoCatalogEntry {
  id: string;
  youtube_video_id: string;
  title: string;
  description: string;
  category: "product" | "tutorial" | "update";
  sort_order: number;
  is_published: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface VideoCatalogCreate {
  youtube_video_id: string;
  title: string;
  description?: string;
  category?: "product" | "tutorial" | "update";
  sort_order?: number;
}

export interface VideoCatalogUpdate {
  title?: string;
  description?: string;
  category?: "product" | "tutorial" | "update";
  sort_order?: number;
  is_published?: boolean;
}

// Public: get published videos
export async function getPublishedVideos(): Promise<VideoCatalogEntry[]> {
  return videoRequest<VideoCatalogEntry[]>("/api/v1/videos");
}

// Admin: get all videos
export async function getAdminVideos(): Promise<VideoCatalogEntry[]> {
  return videoRequest<VideoCatalogEntry[]>("/api/v1/admin/videos");
}

// Admin: create video
export async function createVideo(
  data: VideoCatalogCreate,
): Promise<VideoCatalogEntry> {
  return videoRequest<VideoCatalogEntry>("/api/v1/admin/videos", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Admin: update video
export async function updateVideo(
  id: string,
  data: VideoCatalogUpdate,
): Promise<VideoCatalogEntry> {
  return videoRequest<VideoCatalogEntry>(`/api/v1/admin/videos/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// Admin: delete video
export async function deleteVideo(id: string): Promise<void> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const correlationId = generateCorrelationId();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(`${AUTH_API_BASE}/api/v1/admin/videos/${id}`, {
    method: "DELETE",
    headers,
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error((data as { error: string }).error || "Delete failed");
  }
}
